import React, { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  CircularProgress
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import {
  CloseOutlined,
  SaveOutlined
} from "@mui/icons-material";
import ImageUploadButton from "../ImageUploadButton";
import { useTranslation } from "react-i18next";

// Professional color palette matching the card component
const colors = {
  primary: '#0017E7',
  primaryLight: '#3347FF',
  primaryDark: '#0012B8',
  success: '#00C853',
  warning: '#FF9800',
  error: '#F44336',
  text: '#1A1A1A',
  textLight: '#616161',
  textMuted: '#9E9E9E',
  surface: '#FFFFFF',
  surfaceAlt: '#F5F7FA',
  border: '#E0E0E0',
  white: '#FFFFFF',
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    overflow: 'visible',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.25rem',
  color: colors.text,
  padding: theme.spacing(2.5, 3),
  borderBottom: `1px solid ${colors.border}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: colors.surfaceAlt,
}));

const HeaderBar = styled(Box)(({ userType }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: 4,
  background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
  borderRadius: '12px 12px 0 0',
}));

const FieldRow = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2.5)
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '0.875rem',
  color: colors.text,
  marginTop: theme.spacing(2.5),
  marginBottom: theme.spacing(1.5),
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  display: 'flex',
  alignItems: 'center',
  '&::before': {
    content: '""',
    display: 'inline-block',
    width: 3,
    height: 16,
    background: `linear-gradient(180deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
    marginRight: theme.spacing(1),
    borderRadius: 2,
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    background: colors.white,
    '& fieldset': {
      borderColor: colors.border,
    },
    '&:hover fieldset': {
      borderColor: colors.primary,
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.primary,
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    color: colors.textMuted,
    fontWeight: 500,
    fontSize: '0.875rem',
    '&.Mui-focused': {
      color: colors.primary,
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 14px',
    fontSize: '0.875rem',
    color: colors.text,
  },
}));

const CloseButton = styled(Button)(({ theme }) => ({
  minWidth: 36,
  width: 36,
  height: 36,
  padding: 0,
  borderRadius: 8,
  color: colors.textMuted,
  '&:hover': {
    background: alpha(colors.error, 0.08),
    color: colors.error,
  }
}));

const CancelButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(1, 2.5),
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '0.9rem',
  color: colors.textLight,
  '&:hover': {
    background: alpha(colors.text, 0.05),
  },
  '&:disabled': {
    color: colors.textMuted,
  }
}));

const SaveButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(1, 2.5),
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '0.9rem',
  boxShadow: 'none',
  background: colors.primary,
  color: colors.white,
  '&:hover': {
    background: colors.primaryDark,
    boxShadow: `0 4px 12px ${alpha(colors.primary, 0.3)}`,
  },
  '&:disabled': {
    background: colors.border,
    color: colors.textMuted,
  }
}));

const defaultEditableKeys = [
  'username',
  'email',
  'password',
  'first_name',
  'last_name',
  'image'
];

const EditRecruiterDialog = ({
  open,
  onClose,
  item,
  onUpdated
}) => {
  const { t } = useTranslation();
  const [localForm, setLocalForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && item) {
      const initial = {};
      for (const key of defaultEditableKeys) initial[key] = item[key] ?? '';
      setLocalForm(initial);
    }
  }, [open, item]);

  const handleChange = (field) => (e) => {
    setLocalForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const updateEndpoint = useMemo(() => {
    if (!item?.id) return null;
    const base = process.env.REACT_APP_API_URL || '';
    return `${base}authentication/recruiters/${item.id}/`;
  }, [item]);

  const handleSave = async () => {
    if (!updateEndpoint) return;
    try {
      setSaving(true);
      const token = localStorage.getItem('accessToken');
      console.log('[EditRecruiterDialog] Submitting update', {
        endpoint: updateEndpoint,
        method: 'PUT',
        hasToken: Boolean(token),
        payload: localForm,
        imageValue: localForm.image
      });
      const payload = { ...localForm };
      if (typeof payload.password === 'string' && payload.password.trim() === '') {
        delete payload.password;
      }
      const res = await fetch(updateEndpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        let errorBody = null;
        try {
          errorBody = await res.json();
        } catch (e) {
          try {
            errorBody = await res.text();
          } catch (_) {
            errorBody = null;
          }
        }
        console.error('[EditRecruiterDialog] Update failed', {
          status: res.status,
          statusText: res.statusText,
          url: res.url,
          errorBody
        });
        throw new Error('Failed to update');
      }
      const updated = await res.json().catch(() => ({ ...item, ...localForm }));
      onUpdated && onUpdated(updated);
      onClose && onClose();
    } catch (err) {
      console.error('[EditRecruiterDialog] Error during save', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <StyledDialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <HeaderBar userType="recruiter" />

      <StyledDialogTitle>
        <span>{t('superAdmin.editRecruiterDialog.editRecruiter')}</span>
        <CloseButton onClick={onClose} disabled={saving}>
          <CloseOutlined fontSize="small" />
        </CloseButton>
      </StyledDialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mt: 0.5 }}>
          <SectionTitle>{t('superAdmin.editRecruiterDialog.accountInformation')}</SectionTitle>
          <FieldRow direction="row">
            <StyledTextField
              fullWidth
              label={t('superAdmin.editRecruiterDialog.username')}
              value={localForm.username || ''}
              onChange={handleChange('username')}
              disabled={saving}
            />
            <StyledTextField
              fullWidth
              type="email"
              label={t('superAdmin.editRecruiterDialog.email')}
              value={localForm.email || ''}
              onChange={handleChange('email')}
              disabled={saving}
            />
          </FieldRow>
          <FieldRow direction="row">
            <StyledTextField
              fullWidth
              type="password"
              label={t('superAdmin.editRecruiterDialog.passwordLeaveBlank')}
              value={localForm.password || ''}
              onChange={handleChange('password')}
              disabled={saving}
              placeholder="••••••••"
            />
          </FieldRow>

          <SectionTitle>{t('superAdmin.editRecruiterDialog.personalDetails')}</SectionTitle>
          <FieldRow direction="row">
            <StyledTextField
              fullWidth
              label={t('superAdmin.editRecruiterDialog.firstName')}
              value={localForm.first_name || ''}
              onChange={handleChange('first_name')}
              disabled={saving}
            />
            <StyledTextField
              fullWidth
              label={t('superAdmin.editRecruiterDialog.lastName')}
              value={localForm.last_name || ''}
              onChange={handleChange('last_name')}
              disabled={saving}
            />
          </FieldRow>

          <FieldRow direction="row">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: colors.text }}>{t('superAdmin.editRecruiterDialog.profileImage')}</Typography>
              <ImageUploadButton
                defaultValue={localForm.image}
                onImageUpload={(url) => {
                  setLocalForm((prev) => ({ ...prev, image: url }));
                }}
              />
            </Box>
          </FieldRow>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${colors.border}`, background: colors.surfaceAlt, gap: 1 }}>
        <CancelButton onClick={onClose} disabled={saving}>
          {t('superAdmin.editRecruiterDialog.cancel')}
        </CancelButton>
        <SaveButton onClick={handleSave} variant="contained" disabled={saving} startIcon={saving ? null : <SaveOutlined />}>
          {saving ? (
            <>
              <CircularProgress size={18} sx={{ mr: 1, color: colors.white }} />
              {t('superAdmin.editRecruiterDialog.saving')}
            </>
          ) : (
            t('superAdmin.editRecruiterDialog.saveChanges')
          )}
        </SaveButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default EditRecruiterDialog;